<template>
  <div>
    <div v-for="type in model.options" :key="type.id" class="mb-4">
      <section-title :content="type.name" has-line />

      <div class="mb-3 partial-time">
        <validation-provider v-slot="{ errors }" rules="required|min_value:1">
          <custom-input
            v-if="getTimeOption(type)"
            v-model="getTimeOption(type).other"
            custom-error-msg="El campo es requerido y debe ser mayor a 0"
            :disabled="isReadOnly"
            :error="errors[0]"
            :label="`${getTimeOption(type)?.name || ''}:`"
            min="1"
            placeholder="0"
            type="number"
          />
        </validation-provider>
      </div>

      <div class="row">
        <div v-for="level in levels" :key="level" class="col-md-6 mb-3">
          <div class="level-card" :class="`level-${level}`">
            <div class="level-card__header">Nivel {{ level }}</div>

            <validation-provider
              v-slot="{ errors }"
              rules="required|min_value:1"
            >
              <input
                type="hidden"
                :value="getLevelValidationValue(type, level)"
              />

              <div class="level-card__body">
                <div
                  v-for="option in getLevelOptions(type, level)"
                  :key="option.id"
                  class="level-row"
                >
                  <label class="mb-0">{{ option.name }}:</label>

                  <div class="level-input-wrapper">
                    <custom-input
                      :disabled="isReadOnly"
                      min="0"
                      placeholder="0"
                      type="number"
                      :value="option.other"
                      @input="(newValue) => handleLevelInput(option, newValue)"
                    />
                  </div>
                </div>

                <small v-if="errors[0]" class="level-error">
                  Debe completar al menos un campo del nivel con un valor mayor
                  a 0.
                </small>
              </div>
            </validation-provider>
          </div>
        </div>
      </div>

      <div v-if="getCommentOption(type)" class="mt-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-text-area
            v-model="getCommentOption(type).other"
            :count-classes="['d-block', 'small-counter', 'text-right']"
            custom-error-msg="El campo es requerido"
            :disabled="isReadOnly"
            :error="errors[0]"
            :label="
              `${getCommentOption(type)?.name ||
                'Interpretación de los resultados'}:`
            "
            :maxlength="5000"
            :show-char-count="!isReadOnly"
          />
        </validation-provider>
      </div>
    </div>

    <div class="total-box mt-3 mb-3">
      <span>
        Tiempo total en segundos en el área de Lenguajes y Matemáticas:
      </span>
      <strong>{{ totalTime }}</strong>
    </div>
  </div>
</template>

<script>
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomInput from 'CustomInput';
import CustomTextArea from 'CustomTextArea';

export default {
  name: 'BasicSkillsTest',
  components: {
    SectionTitle,
    CustomInput,
    CustomTextArea,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        options: [],
      }),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      levels: [1, 2, 3, 4],
    };
  },
  computed: {
    model: {
      get() {
        return this.value || { options: [] };
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    totalTime() {
      const lenguaje = this.model.options?.find((x) => x.id === 51);
      const matematicas = this.model.options?.find((x) => x.id === 52);

      const lenguajeTime = Number(this.getTimeOption(lenguaje)?.other || 0);
      const matematicasTime = Number(
        this.getTimeOption(matematicas)?.other || 0
      );

      return lenguajeTime + matematicasTime;
    },
  },
  methods: {
    getTimeOption(type) {
      if (!type || !type.options) return null;

      const option = type.options.find((x) => {
        const name = x.name?.toLowerCase() || '';
        return name.includes('tiempo parcial');
      });

      return option || null;
    },

    getCommentOption(type) {
      if (!type || !type.options) return null;

      const option = type.options.find((x) => {
        const name = x.name?.toLowerCase() || '';
        return name.includes('interpret');
      });

      return option || null;
    },

    getLevelOptions(type, level) {
      if (!type || !type.options) return [];

      return type.options
        .filter((x) => {
          const name = x.name?.toLowerCase() || '';

          return (
            Number(x.group) === level &&
            !name.includes('tiempo parcial') &&
            !name.includes('interpret')
          );
        })
        .map((x) => {
          if (x.other == null) x.other = '';
          return x;
        });
    },

    getLevelValidationValue(type, level) {
      const options = this.getLevelOptions(type, level);
      const hasValidValue = options.some((x) => Number(x.other) > 0);

      return hasValidValue ? 1 : '';
    },

    handleLevelInput(option, newValue) {
      if (newValue === '' || newValue === null || newValue === undefined) {
        option.other = '';
        return;
      }

      const parsed = Number(newValue);

      if (Number.isNaN(parsed)) {
        option.other = '';
        return;
      }
      option.other = String(Math.max(0, parsed));
    },
  },
};
</script>

<style lang="scss" scoped>
.partial-time {
  max-width: 400px;
}

.level-card {
  border: 1px solid #ddd;
  border-radius: 5px;
  overflow: hidden;
  background: #fff;
}

.level-card__header {
  color: #fff;
  font-weight: 600;
  text-align: center;
  padding: 0.75rem;
}

.level-card__body {
  padding: 1.25rem;
}

.level-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.level-input-wrapper {
  width: 70px;
  min-width: 70px;
}

::v-deep .level-input-wrapper input {
  text-align: center;
  height: 34px;
  padding: 0.25rem 0.4rem;
}

.level-error {
  display: block;
  color: #ff5b5c;
  font-size: 14px;
  margin-top: 0.25rem;
  font-weight: 400;
}

.level-1 {
  .level-card__header {
    background: #2db782;
  }

  .level-card__body {
    background: #cef7e2;
  }
}

.level-2 {
  .level-card__header {
    background: #f3af54;
  }

  .level-card__body {
    background: #fde5c6;
  }
}

.level-3 {
  .level-card__header {
    background: #3f5b60;
  }

  .level-card__body {
    background: #d9e5df;
  }
}

.level-4 {
  .level-card__header {
    background: #ff7058;
  }

  .level-card__body {
    background: #fde9e5;
  }
}

.total-box {
  display: inline-flex;
  align-items: center;
  background: #3f5b60;
  color: #fff;
  border-radius: 4px;
  overflow: hidden;

  span {
    padding: 0.65rem 0.9rem;
    font-weight: 600;
  }

  strong {
    background: #d9e5df;
    color: #3f5b60;
    padding: 0.65rem 1rem;
    min-width: 55px;
    text-align: center;
  }
}
</style>
